import {
  CMMD_WORK_INTAKE_FRESH_MS,
  CMMD_WORK_INTAKE_MAX_STALE_MS,
} from "@cmmd-forge/shared/cmmdWorkIntakeCachePolicy";

import type {
  CommandCenterActiveFilterInput,
  CommandCenterEmptyStateContent,
  CommandCenterFilterableItem,
  CommandCenterFilters,
  CommandCenterFreshnessIndicator,
  CommandCenterOption,
  CommandCenterScopedCampaign,
  CommandCenterScopedItem,
  CommandCenterScopedProject,
  CommandCenterScopedTask,
  CommandCenterTaskMeta,
} from "./NoActiveThreadState.logic";
import { projectDisplayName } from "./projects/projectDisplayName";

interface CommandCenterDisconnectedSurfaceInput {
  readonly isLoading: boolean;
  readonly filteredItemCount: number;
  readonly hasCmmdContext: boolean;
}

const CMMD_AUTH_REQUIRED_PATTERNS: ReadonlyArray<RegExp> = [
  /\bCMMD_AUTH_(REQUIRED|EXPIRED)\b/i,
  /\bcmmd\b.*\bauthentication\b.*\b(required|expired)\b/i,
  /\bcmmd\b.*\bsign in\b/i,
  /\bcmmd\b.*\breconnect\b/i,
  /\bno_stored_token\b/i,
];

export function hasActiveCommandCenterFilters(input: CommandCenterActiveFilterInput): boolean {
  return (
    input.activeWorkspaceId !== null ||
    input.teamFilter !== "all" ||
    input.campaignFilter !== "all" ||
    input.projectFilter !== "all" ||
    input.statusFilter !== "all" ||
    input.assigneeFilter !== "all" ||
    input.searchQuery.trim().length > 0 ||
    input.readyOnly
  );
}

export function resolveCommandCenterEmptyStateContent(input: {
  readonly hasActiveFilters: boolean;
  readonly activeWorkspaceName: string | null;
  readonly hasCmmdContext: boolean;
}): CommandCenterEmptyStateContent {
  if (input.hasActiveFilters) {
    return {
      title: "No tasks match the current filters",
      description:
        input.activeWorkspaceName !== null
          ? `No work items matched in ${input.activeWorkspaceName}. Clear filters to view all command work.`
          : "Try broadening project, team, campaign, project, status, or assignee filters.",
      actionLabel: "Clear filters",
    };
  }

  if (!input.hasCmmdContext) {
    return {
      title: "CMMD sync is not connected",
      description: "Connect CMMD in Settings to load campaigns, projects, and tasks into Projects.",
      actionLabel: "Retry sync",
    };
  }

  return {
    title: "No CMMD work yet",
    description:
      "No campaigns, projects, or tasks are currently available. Refresh sync to check for new work.",
    actionLabel: "Refresh sync",
  };
}

function isDisconnectedEmptyStateVisible(input: CommandCenterDisconnectedSurfaceInput): boolean {
  return !input.isLoading && input.filteredItemCount === 0 && !input.hasCmmdContext;
}

export function shouldRenderCommandCenterErrorBanner(
  input: CommandCenterDisconnectedSurfaceInput & { readonly compactError: string | null },
): boolean {
  if (!input.compactError) return false;
  if (!input.isLoading && input.filteredItemCount === 0) {
    return false;
  }
  return !isDisconnectedEmptyStateVisible(input);
}

export function shouldRenderCommandCenterFreshnessIndicator(
  input: CommandCenterDisconnectedSurfaceInput,
): boolean {
  return !isDisconnectedEmptyStateVisible(input);
}

function formatRelativeAge(ageMs: number): string {
  if (ageMs < 60_000) {
    return "just now";
  }
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

export function resolveCommandCenterFreshnessIndicator(input: {
  readonly connected: boolean;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly fetchedAt: string | null;
  readonly nowMs: number;
}): CommandCenterFreshnessIndicator {
  if (input.isLoading && !input.fetchedAt) {
    return {
      tone: "syncing",
      label: "Syncing",
      title: "Loading live CMMD data.",
    };
  }

  if (!input.connected) {
    return {
      tone: "offline",
      label: "Offline",
      title: input.error?.trim().length
        ? `CMMD disconnected: ${input.error}`
        : "CMMD is disconnected.",
    };
  }

  if (!input.fetchedAt) {
    return {
      tone: input.isLoading ? "syncing" : "stale",
      label: input.isLoading ? "Syncing" : "Stale",
      title: input.isLoading
        ? "Waiting for the first CMMD sync snapshot."
        : "No CMMD sync snapshot has been received yet.",
    };
  }

  const fetchedAtMs = Date.parse(input.fetchedAt);
  if (!Number.isFinite(fetchedAtMs)) {
    return {
      tone: "stale",
      label: "Stale",
      title: "Last sync timestamp is invalid. Refresh to recover.",
    };
  }

  const ageMs = Math.max(0, input.nowMs - fetchedAtMs);
  if (input.isLoading) {
    return {
      tone: "syncing",
      label: "Syncing",
      title: `Last CMMD sync ${formatRelativeAge(ageMs)}. Refreshing now.`,
    };
  }

  if (ageMs <= CMMD_WORK_INTAKE_FRESH_MS) {
    return {
      tone: "live",
      label: "Live",
      title: `Last CMMD sync ${formatRelativeAge(ageMs)}.`,
    };
  }

  if (ageMs <= CMMD_WORK_INTAKE_MAX_STALE_MS) {
    return {
      tone: "stale",
      label: "Stale",
      title: `Last CMMD sync ${formatRelativeAge(ageMs)}. Cached data may be stale.`,
    };
  }

  return {
    tone: "out-of-date",
    label: "Out of date",
    title: `Last CMMD sync ${formatRelativeAge(ageMs)}. Reconnect or refresh required.`,
  };
}

export function isCommandCenterCmmdAuthRequiredError(error: string | null): boolean {
  const value = error?.trim();
  if (!value) return false;
  return CMMD_AUTH_REQUIRED_PATTERNS.some((pattern) => pattern.test(value));
}

function resolveDisplayStatus(
  item: Pick<CommandCenterFilterableItem, "source" | "status" | "agentStatus">,
): string {
  if (item.source.startsWith("cmmd-")) {
    const normalized = item.status?.trim().toLowerCase() ?? "todo";
    if (
      ["backlog", "todo", "in_progress", "in_review", "blocked", "done", "cancelled"].includes(
        normalized,
      )
    ) {
      return normalized;
    }
    if (["complete", "completed", "closed"].includes(normalized)) return "done";
    return "todo";
  }
  switch (item.agentStatus) {
    case "running":
      return "in_progress";
    case "pending-approval":
    case "pending-input":
      return "in_review";
    case "error":
      return "blocked";
    default:
      return "todo";
  }
}

function normalizeSearchToken(value: string): string {
  return value.trim().toLowerCase();
}

export function matchesCommandCenterSearch(input: {
  readonly item: {
    readonly title?: string;
    readonly status: string | null;
    readonly priority?: string | null;
    readonly projectName?: string;
    readonly campaignName?: string | null;
    readonly workspaceName?: string | null;
    readonly teamName?: string | null;
  };
  readonly query: string;
  readonly assigneeLabel: string | null;
}): boolean {
  const query = normalizeSearchToken(input.query);
  if (query.length === 0) return true;
  const haystack = [
    input.item.title,
    input.item.status,
    input.item.priority,
    input.item.projectName,
    input.item.campaignName,
    input.item.workspaceName,
    input.item.teamName,
    input.assigneeLabel,
  ]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function resolveAssigneeLabel(input: {
  readonly assigneeId: string | null;
  readonly assigneeLabelById: ReadonlyMap<string, string>;
}): string | null {
  if (!input.assigneeId) return null;
  return input.assigneeLabelById.get(input.assigneeId) ?? input.assigneeId;
}

function matchesWorkspaceFilter(
  item: CommandCenterFilterableItem,
  filters: CommandCenterFilters,
  taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>,
): boolean {
  if (filters.activeWorkspaceId === null) return true;
  if (item.id.startsWith("manual:")) return false;

  if (item.source.startsWith("cmmd-")) {
    const workspaceId = taskMetaByWorkItemId.get(item.id)?.workspaceId ?? item.workspaceId ?? null;
    return workspaceId === filters.activeWorkspaceId;
  }

  return item.environmentId === filters.activeWorkspaceId;
}

function matchesCmmdScopedFilter(input: {
  readonly item: CommandCenterFilterableItem;
  readonly selected: string;
  readonly unassignedValue: string;
  readonly metaValue: string | null;
}): boolean {
  if (input.selected === "all") return true;
  if (input.item.id.startsWith("manual:")) return input.selected === input.unassignedValue;
  if (!input.item.source.startsWith("cmmd-")) return false;
  if (input.selected === input.unassignedValue) return input.metaValue === null;
  return input.metaValue === input.selected;
}

function matchesTeamFilter(
  item: CommandCenterFilterableItem,
  filters: CommandCenterFilters,
  taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>,
): boolean {
  const teamId = taskMetaByWorkItemId.get(item.id)?.teamId ?? item.teamId ?? null;
  return matchesCmmdScopedFilter({
    item,
    selected: filters.teamFilter,
    unassignedValue: "unassigned",
    metaValue: teamId,
  });
}

function matchesCampaignFilter(
  item: CommandCenterFilterableItem,
  filters: CommandCenterFilters,
  taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>,
): boolean {
  return matchesCmmdScopedFilter({
    item,
    selected: filters.campaignFilter,
    unassignedValue: "none",
    metaValue: taskMetaByWorkItemId.get(item.id)?.campaignId ?? null,
  });
}

function matchesProjectFilter(
  item: CommandCenterFilterableItem,
  filters: CommandCenterFilters,
  taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>,
): boolean {
  return matchesCmmdScopedFilter({
    item,
    selected: filters.projectFilter,
    unassignedValue: "none",
    metaValue: taskMetaByWorkItemId.get(item.id)?.projectId ?? null,
  });
}

function matchesAssigneeFilter(input: {
  readonly item: CommandCenterFilterableItem;
  readonly filters: CommandCenterFilters;
  readonly assigneeId: string | null;
}): boolean {
  if (input.filters.assigneeFilter === "all") return true;
  if (input.item.id.startsWith("manual:")) return input.filters.assigneeFilter === "unassigned";
  if (input.item.source !== "cmmd-task") return false;
  if (input.filters.assigneeFilter === "unassigned") return input.assigneeId === null;
  return input.assigneeId === input.filters.assigneeFilter;
}

function matchesCommandCenterFilters(input: {
  readonly item: CommandCenterFilterableItem;
  readonly filters: CommandCenterFilters;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
  readonly assigneeId: string | null;
  readonly assigneeLabelById: ReadonlyMap<string, string>;
}): boolean {
  const { item, filters, taskMetaByWorkItemId, assigneeId, assigneeLabelById } = input;

  if (
    !matchesCommandCenterSearch({
      item,
      query: filters.searchQuery,
      assigneeLabel: resolveAssigneeLabel({ assigneeId, assigneeLabelById }),
    })
  ) {
    return false;
  }

  if (!matchesWorkspaceFilter(item, filters, taskMetaByWorkItemId)) return false;
  if (!matchesTeamFilter(item, filters, taskMetaByWorkItemId)) return false;
  if (!matchesCampaignFilter(item, filters, taskMetaByWorkItemId)) return false;
  if (!matchesProjectFilter(item, filters, taskMetaByWorkItemId)) return false;
  if (filters.readyOnly && !item.isReady) return false;
  if (filters.statusFilter !== "all" && resolveDisplayStatus(item) !== filters.statusFilter) {
    return false;
  }
  return matchesAssigneeFilter({ item, filters, assigneeId });
}

export function applyCommandCenterFilters<T extends CommandCenterFilterableItem>(input: {
  readonly items: readonly T[];
  readonly filters: CommandCenterFilters;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
  readonly assigneeByWorkItemId: ReadonlyMap<string, string | null>;
  readonly assigneeLabelById?: ReadonlyMap<string, string>;
}): readonly T[] {
  const {
    items,
    filters,
    taskMetaByWorkItemId,
    assigneeByWorkItemId,
    assigneeLabelById = new Map<string, string>(),
  } = input;
  return items.filter((item) => {
    const assigneeId = assigneeByWorkItemId.get(item.id) ?? null;
    return matchesCommandCenterFilters({
      item,
      filters,
      taskMetaByWorkItemId,
      assigneeId,
      assigneeLabelById,
    });
  });
}

export function scopeCommandCenterItemsByWorkspace<T extends CommandCenterScopedItem>(input: {
  readonly items: readonly T[];
  readonly activeWorkspaceId: string | null;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (!input.activeWorkspaceId) return input.items;
  return input.items.filter((item) => {
    if (item.id.startsWith("manual:")) return false;
    if (item.source.startsWith("cmmd-")) {
      return (
        (input.taskMetaByWorkItemId.get(item.id)?.workspaceId ?? item.workspaceId ?? null) ===
        input.activeWorkspaceId
      );
    }
    return item.environmentId === input.activeWorkspaceId;
  });
}

export function scopeCommandCenterTasksByWorkspace<T extends CommandCenterScopedTask>(input: {
  readonly tasks: readonly T[];
  readonly activeWorkspaceId: string | null;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (!input.activeWorkspaceId) return input.tasks;
  return input.tasks.filter(
    (task) =>
      (input.taskMetaByWorkItemId.get(`cmmd:${task.id}`)?.workspaceId ?? null) ===
      input.activeWorkspaceId,
  );
}

export function scopeCommandCenterProjectsByWorkspace<T extends CommandCenterScopedProject>(input: {
  readonly projects: readonly T[];
  readonly activeWorkspaceId: string | null;
}): readonly T[] {
  if (!input.activeWorkspaceId) return input.projects;
  return input.projects.filter(
    (project) => (project.workspaceId ?? null) === input.activeWorkspaceId,
  );
}

export function scopeCommandCenterItemsByTeam<T extends CommandCenterScopedItem>(input: {
  readonly items: readonly T[];
  readonly teamFilter: string;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (input.teamFilter === "all") return input.items;
  return input.items.filter((item) => {
    if (item.id.startsWith("manual:")) return input.teamFilter === "unassigned";
    if (!item.source.startsWith("cmmd-")) return false;
    const teamId = input.taskMetaByWorkItemId.get(item.id)?.teamId ?? item.teamId ?? null;
    if (input.teamFilter === "unassigned") return teamId === null;
    return teamId === input.teamFilter;
  });
}

export function scopeCommandCenterTasksByTeam<T extends CommandCenterScopedTask>(input: {
  readonly tasks: readonly T[];
  readonly teamFilter: string;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (input.teamFilter === "all") return input.tasks;
  return input.tasks.filter((task) => {
    const teamId = input.taskMetaByWorkItemId.get(`cmmd:${task.id}`)?.teamId ?? null;
    if (input.teamFilter === "unassigned") return teamId === null;
    return teamId === input.teamFilter;
  });
}

export function scopeCommandCenterProjectsByTeam<T extends CommandCenterScopedProject>(input: {
  readonly projects: readonly T[];
  readonly teamFilter: string;
}): readonly T[] {
  if (input.teamFilter === "all") return input.projects;
  return input.projects.filter((project) => {
    const teamId = project.teamId ?? null;
    if (input.teamFilter === "unassigned") return teamId === null;
    return teamId === input.teamFilter;
  });
}

export function buildCommandCenterTeamOptions(input: {
  readonly tasks: readonly CommandCenterScopedTask[];
  readonly projects: readonly CommandCenterScopedProject[];
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly CommandCenterOption[] {
  const values = new Map<string, string>();
  for (const task of input.tasks) {
    const teamId = input.taskMetaByWorkItemId.get(`cmmd:${task.id}`)?.teamId ?? null;
    if (!teamId) continue;
    values.set(teamId, task.teamName?.trim() || teamId);
  }
  for (const project of input.projects) {
    if (!project.teamId) continue;
    values.set(project.teamId, project.teamName?.trim() || project.teamId);
  }
  return [
    { value: "all", label: "All teams" },
    { value: "unassigned", label: "Unassigned" },
    ...Array.from(values.entries())
      .map(([value, label]) => ({ value, label }))
      .toSorted((left, right) => left.label.localeCompare(right.label)),
  ];
}

export function buildCommandCenterCampaignOptions(input: {
  readonly items: readonly CommandCenterScopedItem[];
  readonly tasks: readonly CommandCenterScopedTask[];
  readonly projects: readonly CommandCenterScopedProject[];
  readonly campaignsById: ReadonlyMap<string, CommandCenterScopedCampaign>;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly CommandCenterOption[] {
  const values = new Map<string, string>();
  let hasUnassigned = false;
  for (const task of input.tasks) {
    const campaignId = input.taskMetaByWorkItemId.get(`cmmd:${task.id}`)?.campaignId ?? null;
    if (!campaignId) {
      hasUnassigned = true;
      continue;
    }
    const label = input.campaignsById.get(campaignId)?.title ?? task.campaignId ?? campaignId;
    values.set(campaignId, label);
  }
  for (const project of input.projects) {
    const campaignId = project.campaignId ?? null;
    if (!campaignId) {
      hasUnassigned = true;
      continue;
    }
    const label = input.campaignsById.get(campaignId)?.title ?? campaignId;
    values.set(campaignId, label);
  }
  if (input.items.some((item) => item.id.startsWith("manual:"))) hasUnassigned = true;
  return [
    { value: "all", label: "All campaigns" },
    ...(hasUnassigned ? [{ value: "none", label: "No campaign" }] : []),
    ...Array.from(values.entries())
      .map(([value, label]) => ({ value, label }))
      .toSorted((left, right) => left.label.localeCompare(right.label)),
  ];
}

export function buildCommandCenterProjectOptions(input: {
  readonly items: readonly CommandCenterScopedItem[];
  readonly tasks: readonly CommandCenterScopedTask[];
  readonly projects: readonly CommandCenterScopedProject[];
}): readonly CommandCenterOption[] {
  const values = new Map<string, string>();
  for (const project of input.projects) {
    values.set(project.id, projectDisplayName(project) ?? project.title);
  }
  const hasUnassigned =
    input.tasks.some((task) => (task.projectId ?? null) === null) ||
    input.items.some((item) => item.id.startsWith("manual:"));
  return [
    { value: "all", label: "All projects" },
    ...(hasUnassigned ? [{ value: "none", label: "No project" }] : []),
    ...Array.from(values.entries())
      .map(([value, label]) => ({ value, label }))
      .toSorted((left, right) => left.label.localeCompare(right.label)),
  ];
}

export function scopeCommandCenterItemsByCampaign<T extends CommandCenterScopedItem>(input: {
  readonly items: readonly T[];
  readonly campaignFilter: string;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (input.campaignFilter === "all") return input.items;
  return input.items.filter((item) => {
    if (item.id.startsWith("manual:")) return input.campaignFilter === "none";
    if (!item.source.startsWith("cmmd-")) return false;
    const campaignId = input.taskMetaByWorkItemId.get(item.id)?.campaignId ?? null;
    if (input.campaignFilter === "none") return campaignId === null;
    return campaignId === input.campaignFilter;
  });
}

export function scopeCommandCenterTasksByCampaign<T extends CommandCenterScopedTask>(input: {
  readonly tasks: readonly T[];
  readonly campaignFilter: string;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (input.campaignFilter === "all") return input.tasks;
  return input.tasks.filter((task) => {
    const campaignId = input.taskMetaByWorkItemId.get(`cmmd:${task.id}`)?.campaignId ?? null;
    if (input.campaignFilter === "none") return campaignId === null;
    return campaignId === input.campaignFilter;
  });
}

export function scopeCommandCenterProjectsByCampaign<T extends CommandCenterScopedProject>(input: {
  readonly projects: readonly T[];
  readonly campaignFilter: string;
}): readonly T[] {
  if (input.campaignFilter === "all") return input.projects;
  return input.projects.filter((project) => {
    const campaignId = project.campaignId ?? null;
    if (input.campaignFilter === "none") return campaignId === null;
    return campaignId === input.campaignFilter;
  });
}

export function scopeCommandCenterItemsByProject<T extends CommandCenterScopedItem>(input: {
  readonly items: readonly T[];
  readonly projectFilter: string;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (input.projectFilter === "all") return input.items;
  return input.items.filter((item) => {
    if (item.id.startsWith("manual:")) return input.projectFilter === "none";
    if (!item.source.startsWith("cmmd-")) return false;
    const projectId = input.taskMetaByWorkItemId.get(item.id)?.projectId ?? null;
    if (input.projectFilter === "none") return projectId === null;
    return projectId === input.projectFilter;
  });
}

export function scopeCommandCenterTasksByProject<T extends CommandCenterScopedTask>(input: {
  readonly tasks: readonly T[];
  readonly projectFilter: string;
  readonly taskMetaByWorkItemId: ReadonlyMap<string, CommandCenterTaskMeta>;
}): readonly T[] {
  if (input.projectFilter === "all") return input.tasks;
  return input.tasks.filter((task) => {
    const projectId = input.taskMetaByWorkItemId.get(`cmmd:${task.id}`)?.projectId ?? null;
    if (input.projectFilter === "none") return projectId === null;
    return projectId === input.projectFilter;
  });
}

export function buildCommandCenterAssigneeOptions(input: {
  readonly tasks: readonly CommandCenterScopedTask[];
}): readonly CommandCenterOption[] {
  const uniqueAssignees = new Set<string>();
  for (const task of input.tasks) {
    if (task.assigneeId) uniqueAssignees.add(task.assigneeId);
  }
  return [
    { value: "all", label: "All assignees" },
    { value: "unassigned", label: "Unassigned" },
    ...Array.from(uniqueAssignees)
      .toSorted((left, right) => left.localeCompare(right))
      .map((assigneeId) => ({
        value: assigneeId,
        label: assigneeId,
      })),
  ];
}
